import { useMemo } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import { OutputSection } from 'components/output-section/output-section';
import { AggregatedProductionVolumesChart } from 'components/free-cash-flow';
import type { SlotCategory } from 'types/evaluation';
import {
  activeFilterCountAtom,
  displayAggregationAtom,
  displayDateRangeAtom,
  displayMeasurementTypeAtom,
  mapScopeAtom,
  serverFiltersAtom,
} from './atoms';
import { useRegisterChartExport } from './chart-export-registry';
import { useEval3Page } from './context';
import { useCalcDetail, useCalcLifecycle, useEvalResult } from './hooks';
import {
  aggregationUnit,
  resolveDateRange,
  toAggregatedProductionVolumes,
} from './utils';
import { slotCategoryRecord } from 'utils/slot-categories';
import styles from './production-volumes.module.scss';

/**
 * The four base production streams plotted by the chart (each also gets a cumulative
 * companion series). Hoisted so the `memo`'d chart keeps a stable prop identity across
 * unrelated re-renders.
 */
const PRODUCTION_TYPES = ['oil', 'gas', 'ngl', 'water'] as const;

/**
 * All reserve categories visible — the shared chart still takes the (inert here)
 * `slotCategoryVisibility` prop that its legacy sibling uses; Production Volumes has no
 * per-category legend, so it passes every category on. Hoisted for a stable identity.
 */
const ALL_CATEGORIES_VISIBLE: Record<SlotCategory, boolean> =
  slotCategoryRecord(() => true);

/**
 * Connected Production Volumes section — the aggregated production plot, reusing the
 * shared `AggregatedProductionVolumesChart` (relocated from the legacy eval-3
 * aggregated-free-cash-flow). Reads page identity + the viewed eval's calc-detail (for
 * `status` + `effective_date`) + the dataset filters / spatial scope / display
 * aggregation / measurement type / date range, fetches `useEvalResult`, and reshapes its
 * raw volume arrays into `AggregatedProductionVolumes` via `toAggregatedProductionVolumes`.
 * Mirrors the connected `SlotCountSection`; gated by the Layout Settings `productionVolumes`
 * toggle at the mount site.
 *
 * States — the chart is the PERMANENT fixture; chrome layers above it:
 *  - **Loading** — `isFetching || useCalcLifecycle.isRunning`: on a FIRST load (no data
 *    yet) the chart shows its loading/empty state; on a REFETCH over existing data the
 *    fixture DIMS in place instead (matches the sibling sections). `aria-busy` carries the
 *    a11y signal either way.
 *  - **Error** — a notification + Retry ABOVE the fixture (never replaces it), shown only
 *    when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - **Loaded** — the chart, with a "Filtered view" banner when a filter/scope is active.
 *
 * @return {JSX.Element} The Production Volumes section.
 */
export function ProductionVolumesSection() {
  const { packageId, calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const aggregationKey = useAtomValue(displayAggregationAtom);
  const measurementType = useAtomValue(displayMeasurementTypeAtom);
  const dateRange = useAtomValue(displayDateRangeAtom);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  const status = calcDetail?.status;
  const { data, isFetching, error, refetch } = useEvalResult({
    calcId: calculationId,
    status,
    aggregationKey,
    filters,
    mapScope,
  });

  // The chart's x-axis interval is a moment unit; `aggregationUnit` maps the display
  // aggregation ('monthly'|'quarterly'|'yearly') to the exact `AggregationPeriod` the
  // chart expects. eval3-new's response carries no `interval`, but `useEvalResult` sends
  // `aggregationKey`, so this equals the aggregation the data is in (parity with the
  // legacy view's `resultData.interval`).
  const interval = aggregationUnit(aggregationKey);

  // The eval's applied effective date drives the chart's mid-interval plot line + the
  // date-range math. SNAKE_CASE on the wire (`configurations` is a raw hash value, so its
  // keys aren't camelized). Empty string when absent (no calc / draft).
  const effectiveDate = calcDetail?.configurations?.effective_date ?? '';

  // No calc (workspace / draft): blank explicitly — `keepPreviousData` would otherwise
  // hold the previously-viewed calc's numbers even though the query is disabled. Memoized
  // (like the legacy view's `aggregatedProductionVolumes`) so the `memo`'d chart keeps a
  // stable object identity across unrelated re-renders.
  const rawResult =
    calculationId === null ? undefined : data?.results[0]?.result;
  const aggregatedProductionVolumes = useMemo(
    () => toAggregatedProductionVolumes(rawResult ?? undefined, interval),
    [rawResult, interval]
  );

  // The visible date window from the display settings + the eval's effective date, passed
  // to the chart via its optional `resolvedDateRange` prop so it doesn't fall back to the
  // legacy per-package `useResolveDateRange` hook. Memoized so the `memo`'d chart keeps a
  // stable range identity across unrelated re-renders.
  const resolvedDateRange = useMemo(
    () => resolveDateRange(effectiveDate, aggregationKey, dateRange),
    [effectiveDate, aggregationKey, dateRange]
  );

  // The unified loading umbrella: this section's own eval_result fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — one loading language.
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = isFetching || isRunning;
  const hasData = aggregatedProductionVolumes.productionVolumes.length > 0;

  // Register the Production Volumes chart for content export (group 'productionVolumes').
  // The section is always mounted (CSS-hidden when its Layout toggle is off), and Highcharts
  // captures offscreen from the chart's options — so it stays exportable even when hidden.
  const onExporterReady = useRegisterChartExport({
    id: 'production_volumes',
    name: 'Production Volumes',
    group: 'productionVolumes',
  });

  return (
    <OutputSection
      title="Production Volumes"
      isLoading={isLoading}
      defaultExpanded
    >
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}
      {error && !isLoading && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the results"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}
      {/* First load (no data) → the chart shows its own loading/empty state; refetch over
              existing data → dim the fixture in place. */}
      <div
        className={classNames(styles.content, {
          [styles.dimmed]: isLoading && hasData,
        })}
        aria-busy={isLoading}
      >
        <AggregatedProductionVolumesChart
          packageId={packageId}
          isLoading={isLoading && !hasData}
          aggregatedProductionVolumes={aggregatedProductionVolumes}
          measurementType={measurementType}
          resolvedDateRange={resolvedDateRange}
          productionTypes={PRODUCTION_TYPES}
          effectiveDate={effectiveDate}
          slotCategoryVisibility={ALL_CATEGORIES_VISIBLE}
          onExporterReady={onExporterReady}
        />
      </div>
    </OutputSection>
  );
}
